import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { FormService } from '../../services/form.service';
import { ApiService } from '../../services/api.service';
import { TrackingService } from '../../services/tracking.service';
import { MatDialog } from '@angular/material/dialog';
import { ChangePhoneModalComponent } from '../../form-parts/change-phone-modal/change-phone-modal.component';

@Component( {
	selector: 'app-post-submit-verification',
	templateUrl: './verification.component.html',
	styleUrls: ['./verification.component.scss']
} )
export class PostSubmitVerificationComponent implements OnInit, OnDestroy {

	public verificationForm !: UntypedFormGroup
	public codeLength: number = 6;
	public codeLengthArray = Array.from( { length: this.codeLength } );
	public codeForm !: UntypedFormGroup;
	public submitAttempted = false;
	public verifyLoading = false;
	public verifyError: string | null = null;
	public resendLoading = false;
	public lastResendTime: number | null = null;
	public resendTick = 0;
	private readonly resendCooldownSeconds = 15;
	private resendCooldownInterval: ReturnType<typeof setInterval> | null = null;
	public verifyAttemptTimestamps: number[] = [];
	private readonly verifyRateLimitWindowMs = 30000;
	private readonly verifyAttemptsPerWindow = 10;
	// DEV-6558 telemetry counters: monotonic verify-attempt ordinal and the
	// number of resends the consumer requested during this verification.
	public verifyAttemptCount = 0;
	public resendCount = 0;
	@ViewChildren( 'codeInput' ) public codeInputs !: QueryList<ElementRef<HTMLInputElement>>;

	constructor(
		public formService: FormService,
		private apiService: ApiService,
		private trackingService: TrackingService,
		private dialog: MatDialog
	) {

	}

	get phoneNumber(): string {

		return this.formService.mainForm.get( 'nameEmailPhone.phoneNumberComplete' )?.value || '';
	}

	ngOnInit(): void {

		this.verificationForm = this.formService.verificationForm as UntypedFormGroup;
		this.codeForm = new UntypedFormGroup( {
			digits: new UntypedFormArray(
				Array.from( { length: this.codeLength } ).map( () => new UntypedFormControl( '' ) )
			)
		} );

		this.setDigitsFromValue( this.verificationForm.controls['otp'].value );

		this.getDigitsArray().valueChanges.subscribe( values => {

			const combinedValue = ( values ?? [] ).join( '' );
			const sanitizedValue = this.sanitizeCodeValue( combinedValue );

			if( sanitizedValue !== this.verificationForm.controls['otp'].value ) {

				this.verificationForm.controls['otp'].setValue( sanitizedValue, { emitEvent: false } );
				this.verificationForm.controls['otp'].markAsDirty();
				this.verificationForm.controls['otp'].markAsTouched();
			}
		} );

		this.verificationForm.controls['otp'].valueChanges.subscribe( value => {

			const sanitizedValue = this.sanitizeCodeValue( value );

			if( sanitizedValue !== this.getCodeValueFromInputs() ) {

				this.setDigitsFromValue( sanitizedValue );
			}
		} );

		this.resendCooldownInterval = setInterval( () => {

			this.resendTick = Date.now();
		}, 1000 );
	}

	ngOnDestroy(): void {

		if( this.resendCooldownInterval != null ) {

			clearInterval( this.resendCooldownInterval );
		}
	}

	get resendCooldownRemaining(): number {

		if( this.lastResendTime == null ) {

			return 0;
		}

		const elapsed = Math.floor( ( this.resendTick - this.lastResendTime ) / 1000 );

		return elapsed >= this.resendCooldownSeconds ? 0 : this.resendCooldownSeconds - elapsed;
	}

	get resendAllowed(): boolean {

		return this.resendCooldownRemaining === 0 && !this.resendLoading;
	}

	get verifyCooldownRemaining(): number {

		const now = this.resendTick;
		const cutoff = now - this.verifyRateLimitWindowMs;
		const inWindow = this.verifyAttemptTimestamps.filter( ts => ts > cutoff );

		if( inWindow.length < this.verifyAttemptsPerWindow ) {

			return 0;
		}

		const oldest = inWindow[0];
		const unblockAt = oldest + this.verifyRateLimitWindowMs;
		const remaining = Math.ceil( ( unblockAt - now ) / 1000 );

		return remaining > 0 ? remaining : 0;
	}

	get verifyAllowed(): boolean {

		return this.verifyCooldownRemaining === 0 && !this.verifyLoading;
	}

	onVerifySubmit() {

		this.submitAttempted = true;
		this.verifyError = null;

		if( this.verificationForm.invalid ) {

			Object.keys( this.verificationForm.controls ).forEach( key => {

				this.verificationForm.controls[key].markAsDirty();
				this.verificationForm.controls[key].markAsTouched();
			} );
			return;
		}

		if( !this.verifyAllowed ) {

			return;
		}

		const token = this.formService.token;
		const otp = this.verificationForm.controls['otp'].value;

		if( !token ) {

			this.verifyError = 'Verification is not available. Please try again.';
			return;
		}

		this.formService.logFormAction( 'otp_verify', 'verification' );
		this.formService.trackEventWithContext( 'Verify', 'verification' );
		this.trackingService.clarityEvent( 'otp_enter_code' );
		this.verifyLoading = true;

		const now = Date.now();
		const cutoff = now - this.verifyRateLimitWindowMs;
		this.verifyAttemptTimestamps = this.verifyAttemptTimestamps.filter( ts => ts > cutoff );
		this.verifyAttemptTimestamps.push( now );
		this.verifyAttemptCount += 1;

		this.apiService.postVerifyOtp( token, otp ).subscribe( {
			next: ( response ) => {

				this.verifyLoading = false;

				if( response?.status === 'invalid' ) {

					this.formService.logFormAction( 'otp_verify_fail', 'verification' );
					this.emitVerificationResult( 'failed' );
					this.verifyError = $localize`:@@verification-failed-please-enter-valid-code:Verification failed. Please enter a valid code.`;
					return;
				}

				this.formService.logFormAction( 'otp_verify_success', 'verification' );
				this.emitVerificationResult( 'verified' );
				this.formService.proceedPostSubmitFlow();
			},
			error: () => {

				this.verifyLoading = false;
				this.formService.logFormAction( 'otp_verify_fail', 'verification' );
				this.emitVerificationResult( 'failed' );
				this.verifyError = $localize`:@@verification-failed-please-enter-valid-code:Verification failed. Please enter a valid code.`;
			}
		} );
	}

	/**
	 * DEV-6558: emit the post-submit verification outcome as telemetry. Only
	 * the outcome and the attempt/resend counters are sent — the OTP code is
	 * deliberately never included (no FormGroup is passed).
	 */
	private emitVerificationResult( outcome: 'verified' | 'failed' ): void {

		this.formService.logFormAction( 'verification_result', 'verification', undefined, {
			outcome,
			attempt_number: this.verifyAttemptCount,
			resend_count:   this.resendCount,
		} );
	}

	onVerifySkip() {

		this.formService.skipVerification();
	}

	onResendCode() {

		if( !this.resendAllowed ) {

			return;
		}

		this.formService.logFormAction( 'otp_resend', 'verification' );
		this.resendCount += 1;
		this.formService.trackEventWithContext( 'Resend code', 'verification' );
		this.trackingService.clarityEvent( 'otp_resend_code' );
		const token = this.formService.token;

		if( !token ) {

			return;
		}

		this.resendLoading = true;

		this.apiService.getResendOtp( token ).subscribe( {
			next: () => {

				this.resendLoading = false;
				this.lastResendTime = Date.now();
			},
			error: () => {

				this.resendLoading = false;
			}
		} );
	}

	isInputInvalid( index: number ): boolean {

		if( !this.submitAttempted ) {

			return false;
		}

		const digitControl = this.getDigitsArray().at( index );

		return !digitControl?.value || digitControl.value === '';
	}

	hasDigitValue( index: number ): boolean {

		const digitControl = this.getDigitsArray().at( index );

		return !!digitControl?.value && digitControl.value !== '';
	}

	openChangePhoneModal() {

		this.formService.logFormAction( 'change_phone', 'verification' );
		this.formService.trackEventWithContext( 'Change phone - open', 'verification' );
		this.trackingService.clarityEvent( 'otp_open_modify_phone' );
		const dialogRef = this.dialog.open( ChangePhoneModalComponent, {
			width: '500px',
			panelClass: 'change-phone-dialog',
			data: {
				currentPhone: this.phoneNumber
			}
		} );

		dialogRef.afterClosed().subscribe( result => {

			if( result ) {

				this.formService.logFormAction( 'change_phone_saved', 'verification' );
				const nameEmailPhoneGroup = this.formService.mainForm.get( 'nameEmailPhone' ) as UntypedFormGroup;
				nameEmailPhoneGroup.patchValue( {
					phoneNumber: result.phoneNumber,
					phoneNumberComplete: result.phoneNumberComplete
				} );
			}
		} );
	}

	onCodeInput( index: number, event: Event ) {

		const input = event.target as HTMLInputElement;
		const rawValue = input.value ?? '';
		const sanitizedValue = this.sanitizeCodeValue( rawValue );
		const singleValue = sanitizedValue.length > 0 ? sanitizedValue[sanitizedValue.length - 1] : '';

		if( input.value !== singleValue ) {

			input.value = singleValue;
		}

		const digitsArray = this.getDigitsArray();

		if( digitsArray.at( index )?.value !== singleValue ) {

			digitsArray.at( index )?.setValue( singleValue );
		}

		if( singleValue.length > 0 ) {

			this.focusInput( index + 1 );
		}
	}

	onCodeKeydown( index: number, event: KeyboardEvent ) {

		if( event.key !== 'Backspace' ) {

			return;
		}

		const input = event.target as HTMLInputElement;

		if( input.value.length > 0 ) {

			return;
		}

		this.focusInput( index - 1 );
	}

	onCodeFocus( event: FocusEvent ) {

		const input = event.target as HTMLInputElement;

		if( input.value.length > 0 ) {

			setTimeout( () => {

				input.setSelectionRange( input.value.length, input.value.length );
			}, 0 );
		}
	}

	onCodePaste( event: ClipboardEvent ) {

		event.preventDefault();

		const pasteValue = event.clipboardData?.getData( 'text' ) ?? '';
		const sanitizedValue = this.sanitizeCodeValue( pasteValue );

		if( sanitizedValue.length === 0 ) {

			return;
		}

		this.setDigitsFromValue( sanitizedValue );

		this.focusInput( sanitizedValue.length );
	}

	private sanitizeCodeValue( value: string ) : string {

		const stringValue = ( value ?? '' ).toString();

		return stringValue.replace( /\D/g, '' ).slice( 0, this.codeLength );
	}

	private getInputElements() : HTMLInputElement[] {

		return this.codeInputs?.toArray().map( item => item.nativeElement ) ?? [];
	}

	private getCodeValueFromInputs() : string {

		const inputs = this.getInputElements();

		return inputs.map( input => input.value ).join( '' );
	}

	private getDigitsArray() : UntypedFormArray {

		return this.codeForm.get( 'digits' ) as UntypedFormArray;
	}

	private setDigitsFromValue( value: string ) {

		const sanitizedValue = this.sanitizeCodeValue( value );
		const digitsArray = this.getDigitsArray();
		const inputElements = this.getInputElements();

		for( let index = 0; index < this.codeLength; index++ ) {

			const digit = sanitizedValue[index] ?? '';
			const control = digitsArray.at( index );

			if( control && control.value !== digit ) {

				control.setValue( digit, { emitEvent: false } );
			}

			if( inputElements[index] && inputElements[index].value !== digit ) {

				inputElements[index].value = digit;
			}
		}
	}

	private focusInput( index: number ) {

		const inputs = this.getInputElements();

		if( index < 0 || index >= inputs.length ) {

			return;
		}

		inputs[index].focus();
	}
}
